<template>
  <div class="relative h-full">
    <div
      :class="[
        scrolledToTop ? 'shadow-sm' : 'shadow',
        !isDarkMode ? 'shadow' : '',
      ]"
      class="flex justify-between items-center p-4 lg:px-16 bg-white relative z-60 h-20"
    >
      <div class="w-16 h-16"><TenantLogo /></div>
      <div class="flex items-center space-x-4">
        <div
          v-if="user"
          class="py-1 border-r border-truegray-500 flex items-center justify-center hover:text-primary cursor-pointer transition-all duration-300"
          @click="triggerLogOut"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-4 w-4 mr-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="2"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
            />
          </svg>
        </div>

        <div
          v-if="!user"
          class="py-1 border-r border-truegray-500 flex items-center justify-center hover:text-primary cursor-pointer transition-all duration-300"
          @click="showLoginModal"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-4 w-4 mr-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="2"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"
            />
          </svg>
        </div>

        <div
          v-if="user"
          class="py-1 border-r border-truegray-500 flex items-center justify-center hover:text-primary cursor-pointer transition-all duration-300"
          @click="openAdminMenu"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-4 w-4 mr-4"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              d="M5 4a1 1 0 00-2 0v7.268a2 2 0 000 3.464V16a1 1 0 102 0v-1.268a2 2 0 000-3.464V4zM11 4a1 1 0 10-2 0v1.268a2 2 0 000 3.464V16a1 1 0 102 0V8.732a2 2 0 000-3.464V4zM16 3a1 1 0 011 1v7.268a2 2 0 010 3.464V16a1 1 0 11-2 0v-1.268a2 2 0 010-3.464V4a1 1 0 011-1z"
            />
          </svg>
        </div>

        <div
          class="w-full py-1 flex justify-center items-center hover:text-primary cursor-pointer transition-all duration-300 mr-8"
          @click="callLayoutSpecificAction"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-4 w-4 mr-4"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"
            />
            <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
          </svg>
        </div>

        <div class="pl-8 cursor-pointer" @click="toggleMenu()">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4 6h16M4 12h16M4 18h16"
            />
          </svg>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import TenantLogo from '../../../components/TenantLogo.vue'
import { mapGetters } from 'vuex'
import { EventBus } from '../../../../../../js/plugins/event-bus.js'
import { scrollToElement } from '../../../../../../js/util/helper.js'

export default {
  components: {
    TenantLogo,
  },

  data() {
    return {
      scrolledToTop: true,
    }
  },

  computed: {
    ...mapGetters({
      user: 'shared/getUser',
      customisation: 'careerwise/getCustomisation',
      isDarkMode: 'careerwise/isDarkMode',
    }),
  },

  watch: {
    menuActive: {
      handler: function (val) {
        EventBus.$emit('modal_state_changing', val)
      },

      deep: true,
    },
  },

  mounted() {
    EventBus.$on('toggleMenu', () => {
      this.menuActive = !this.menuActive
      EventBus.$emit('showSmartMenu', this.menuActive)
    })

    EventBus.$on('closeMobileMenu', () => {
      // Old menu - remove in future
      this.$set(this, 'menuActive', false)
    })

    EventBus.$on('closeSmartMenu', () => {
      this.$set(this, 'menuActive', false)
    })

    this.checkWindowPosition()
  },

  methods: {
    triggerLogOut() {
      EventBus.$emit('showModal', 'logout')
    },

    showSubscribeModal() {
      EventBus.$emit('showModal', 'subscribe')
    },

    scrollToContact() {
      // Check if we are on the home page
      if (this.$router.history.current.name !== 'Home') {
        // If not, navigate to the home page
        this.$router.push({ path: '/' }).then(() => {
          // Wait for the navigation to complete before calling helper function
          // also allow some time for the posts to come back to stop dom shift
          // @todo do this better or adjust value once prod speeds can be tested
          setTimeout(() => {
            scrollToElement('contactForm')
          }, 700)
        })
      } else {
        // If we are already on the home page, just call helper function
        scrollToElement('contactForm')
      }
    },

    callLayoutSpecificAction() {
      if (this.customisation.layout === 2) {
        this.scrollToContact()
      } else {
        this.showSubscribeModal()
      }
    },

    showLoginModal() {
      EventBus.$emit('showModal', 'login')
    },

    openAdminMenu() {
      this.toggleMenu()
      EventBus.$emit('openAdminMenu')
    },

    toggleMenu() {
      EventBus.$emit('showSmartMenu', true)
    },

    checkWindowPosition() {
      window.onscroll = () => {
        if (window.scrollY <= 10) {
          this.$set(this, 'scrolledToTop', true)
        } else {
          this.$set(this, 'scrolledToTop', false)
        }
      }
    },
  },
}
</script>

<style scoped>
#mobileMenu {
  height: calc(100vh - 4rem);
  background: rgba(255, 255, 255, 0.97);
}
</style>
